<script setup lang="ts">
// The Clone button of a repository, and what a first-timer needs behind it: the hrth git
// tools, linking the computer as one of their devices (so it gets their keys), then the
// clone command. Encryption happens on the computer, which is why plain git needs the
// helper (docs/DEPLOY.md, "Git with hrth").
import { ref } from 'vue'
import { describeError } from '../api/http'
import { useSession } from '../stores/session'

const props = defineProps<{ cloneUrl: string }>()
const session = useSession()

const open = ref(false)
const link = ref<{ url: string; expiresAt: number } | null>(null)
const copied = ref<string | null>(null)

const install = 'cargo install --path crates/hrth-cli'

async function makeLink(): Promise<void> {
  try {
    const t = await session.api.createLinkToken()
    link.value = { url: t.url, expiresAt: t.expires_at }
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function copy(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text)
    copied.value = text
    setTimeout(() => {
      if (copied.value === text) copied.value = null
    }, 1500)
  } catch {
    session.toast('Couldn’t copy; select the command instead', 'error')
  }
}

const until = (ms: number) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
</script>

<template>
  <div class="-mx-6 border-t border-border px-6 py-4" data-testid="repository-clone">
    <div class="flex flex-wrap items-center gap-3">
      <button class="btn-primary" :aria-expanded="open" data-testid="clone-button" @click="open = !open">Clone</button>
      <code class="mono min-w-0 truncate text-text-muted select-all">{{ props.cloneUrl }}</code>
    </div>

    <ol v-if="open" class="mt-4 max-w-2xl space-y-4 text-sm" data-testid="clone-steps">
      <li>
        <div class="label mb-1">1 · Install the hrth git tools <span class="text-text-muted normal-case">(once per computer)</span></div>
        <p class="mb-2 text-text-muted">
          The code is encrypted on your computer before it leaves, so the server can't read it. Git does that through a
          small helper, <code>git-remote-hrth</code>. From a checkout of hrthd:
        </p>
        <div class="flex items-center gap-2">
          <code class="mono flex-1 border border-border px-3 py-1.5 select-all">{{ install }}</code>
          <button class="btn-ghost px-2" @click="copy(install)">{{ copied === install ? 'Copied' : 'Copy' }}</button>
        </div>
      </li>

      <li>
        <div class="label mb-1">2 · Link the computer to your account <span class="text-text-muted normal-case">(once)</span></div>
        <p class="mb-2 text-text-muted">
          It becomes one of your devices, like a new phone, and this browser hands it your keys. Keep this tab open while
          it links.
        </p>
        <button v-if="!link" class="btn-outline" data-testid="clone-make-link" @click="makeLink">Make a link</button>
        <template v-else>
          <div class="flex items-center gap-2">
            <code class="mono flex-1 truncate border border-border px-3 py-1.5 select-all" data-testid="clone-link-command">hrth link {{ link.url }}</code>
            <button class="btn-ghost px-2" @click="copy(`hrth link ${link.url}`)">{{ copied === `hrth link ${link.url}` ? 'Copied' : 'Copy' }}</button>
          </div>
          <p class="mono mt-1 text-text-muted">Works once, until {{ until(link.expiresAt) }}.</p>
        </template>
      </li>

      <li>
        <div class="label mb-1">3 · Clone</div>
        <div class="flex items-center gap-2">
          <code class="mono flex-1 truncate border border-border px-3 py-1.5 select-all">git clone {{ props.cloneUrl }}</code>
          <button class="btn-ghost px-2" @click="copy(`git clone ${props.cloneUrl}`)">{{ copied === `git clone ${props.cloneUrl}` ? 'Copied' : 'Copy' }}</button>
        </div>
        <p class="mt-2 text-text-muted">After that it's ordinary git: pull, push and branch as usual.</p>
      </li>
    </ol>
  </div>
</template>
